import {
  useGetAccounts,
  useGetApplication,
  useGetContext,
  useGetFormalOffers,
} from '../../../../api/lending/lapiHooks';
import { getIwocaPayApplication } from '../../Dashboard.helpers';

export type NavItem = {
  label: string;
  href: string;
};

export const useGetNavigationData = (): {
  navItems: NavItem[];
  isSeller: boolean;
  isLoading: boolean;
} => {
  const { context, loadingContext } = useGetContext();
  const { accounts, loadingAccounts } = useGetAccounts();
  const { application, loadingApplication } = useGetApplication();
  const { formalOffers, loadingFormalOffers } = useGetFormalOffers();

  const isLoading =
    loadingContext ||
    loadingAccounts ||
    loadingApplication ||
    loadingFormalOffers;
  const isSeller = context?.isSeller ?? false;

  if (!accounts || !application) {
    return { navItems: [], isSeller, isLoading };
  }

  const accountsProductTypes = accounts.accounts
    .filter((a) => a.product_type && a.account_id)
    .map((a) => a.product_type!);

  type ProductType = (typeof accountsProductTypes)[number];
  const has = (type: ProductType) => accountsProductTypes.includes(type);

  const hasIwocaPayApplicationOrFormalOffer =
    !!getIwocaPayApplication(application) ||
    !!(formalOffers?.formal_offers ?? []).some(
      (offer) => offer.product_type === 'iwocapay',
    );

  const hasCreditFacility = has('credit_facility') || has('flexi24');
  const hasSecondCBILS =
    accountsProductTypes.filter((t) => t === 'cbils').length === 2;

  const navItems: NavItem[] = (
    [
      !hasCreditFacility && { label: 'My Offers', href: '/account/my-offers/' },
      has('recovery_loan') && { label: 'RLS', href: '/recovery-loan-scheme/' },
      (has('iwocapay') || hasIwocaPayApplicationOrFormalOffer) && {
        label: 'iwocaPay',
        href: '/pay/account/',
      },
      has('merchant_cash_advance') && { label: 'RBL', href: '/' },
      has('cbils') && {
        label: hasSecondCBILS ? '1st CBILS' : 'CBILS',
        href: '/CBILS/',
      },
      hasSecondCBILS && { label: '2nd CBILS', href: '/CBILS2/' },
      has('business_loan') && {
        label: 'Business Loan',
        href: '/business-loan/',
      },
      hasCreditFacility && {
        label: 'Flexi Loan',
        href: '/flexi-12-dashboard/',
      },
    ] as const
  ).filter(Boolean) as NavItem[];

  return { navItems, isSeller, isLoading };
};
